import type { BedLoop } from "./mixPolicy";
import { bedPosition, loopWindow } from "./mixPolicy";
import { TRACKS } from "./audioCues";
import type { MusicTrack } from "./audioPort";

/**
 * The background beds (audio.ts GameAudio owns one): one looping bed at a time on the music bus, cross-faded, looped
 * at the composed bar length from `music_manifest.json` rather than the decoded buffer's end (so MP3 padding never
 * lands in the seam). Only the bed that is playing stays decoded (about 30 MB of PCM each); the other is kept as its
 * compressed bytes and decoded again on the next switch.
 */
export class MusicBeds {
  static readonly FadeSeconds = 0.9;
  /** Each bed's compressed bytes, kept so a bed dropped after its cross-fade can be decoded again. */
  private readonly bedBytes = new Map<MusicTrack, ArrayBuffer>();
  private readonly beds = new Map<MusicTrack, AudioBuffer>();
  private readonly decoding = new Map<MusicTrack, Promise<AudioBuffer | null>>();
  /** Loop length and MP3 lead (s) per bed from `music_manifest.json`. */
  private readonly loops = new Map<MusicTrack, BedLoop>();
  /** Every bed fetch has finished (or failed): a bed with no bytes by now never gets any. */
  private bedsSettled = false;
  /** The bed on the speakers: `from` is where in the buffer it started, at context time `startedAt`. */
  private playing: { src: AudioBufferSourceNode; gain: GainNode; track: MusicTrack; from: number; startedAt: number; duration: number; span: { start: number; end: number } | null } | null = null;
  /** Bumped when the context is replaced: a decode begun on the old one is not used. */
  private generation = 0;
  private wanted: MusicTrack | null = null;

  /** `out` is the context and the music bus, once the context exists (null before). */
  constructor(private readonly out: () => { ctx: AudioContext; bus: GainNode } | null) {}

  /**
   * Fetches every bed's bytes, the one wanted first (the hub, at boot) so it starts the moment it decodes; one promise
   * per bed, the first one's first. Call `settle` once they have all finished.
   */
  fetch(resolve: (url: string) => string, fetchBytes: (name: string) => Promise<ArrayBuffer | null>): Promise<void>[] {
    const loops = this.loadLoops(resolve);
    const first = this.wanted ?? "hub";
    return [first, ...TRACKS.filter((t) => t !== first)].map(async (track) => {
      const bytes = await fetchBytes(`music_${track}`);
      await loops;
      if (bytes) this.bedBytes.set(track, bytes);
      if (this.wanted === track) this.music(track);
      if (track === first) await this.decodeBed(track);
    });
  }

  /** Every bed fetch has finished: the bed wanted meanwhile starts if it has not. */
  settle(): void {
    this.bedsSettled = true;
    if (this.wanted && this.playing?.track !== this.wanted) this.music(this.wanted);
  }

  private async loadLoops(resolve: (url: string) => string): Promise<void> {
    try {
      const beds = (await (await fetch(resolve("./audio/music_manifest.json"))).json()) as Record<string, { loop?: number; lead?: number }>;
      for (const [track, v] of Object.entries(beds)) if (typeof v?.loop === "number") this.loops.set(track as MusicTrack, { loop: v.loop, lead: typeof v.lead === "number" ? v.lead : 0 });
    } catch { /* no manifest: loop the whole buffer */ }
  }

  /** The bed's decoded buffer, decoding it from its bytes once; null while the bytes have not arrived (or never will). */
  private decodeBed(track: MusicTrack): Promise<AudioBuffer | null> {
    const have = this.beds.get(track);
    if (have) return Promise.resolve(have);
    const pending = this.decoding.get(track);
    if (pending) return pending;
    const bytes = this.bedBytes.get(track), ctx = this.out()?.ctx;
    if (!bytes || !ctx) return Promise.resolve(null);
    // decodeAudioData detaches what it is given: decode a copy so the bytes survive for the next switch.
    const gen = this.generation;
    const job = ctx.decodeAudioData(bytes.slice(0))
      .then((b) => {
        if (gen !== this.generation) return null;                                // the context died meanwhile
        if (this.wanted === null || this.wanted === track) this.beds.set(track, b);  // not a bed switched away from meanwhile
        return b;
      })
      .catch(() => null)
      .finally(() => { if (this.decoding.get(track) === job) this.decoding.delete(track); });
    this.decoding.set(track, job);
    return job;
  }

  get track(): MusicTrack | null { return this.wanted; }
  /** The bed actually on the speakers (null while none is: decoding, faded out, or its source ended). */
  get playingTrack(): MusicTrack | null { return this.playing?.track ?? null; }

  /** `from` (buffer seconds) starts the bed there rather than at its loop start (a restart on a new context). */
  music(track: MusicTrack | null, from?: number): void {
    this.wanted = track;
    const out = this.out();
    if (!out) return;
    const { ctx, bus } = out;
    if (this.playing?.track === track) return;
    const buffer = track ? this.beds.get(track) : undefined;
    if (track && !buffer) {
      // Keep the old bed going while the new one decodes, then cross-fade; if it never can, just fade out.
      const gen = this.generation;
      void this.decodeBed(track).then((b) => {
        if (this.wanted !== track || gen !== this.generation) return;
        if (b) this.music(track, from);
        else if (this.bedsSettled) this.fadeOutBed();
      });
      return;
    }
    this.fadeOutBed();
    if (!track || !buffer) return;
    const now = ctx.currentTime;
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.loop = true;
    const span = loopWindow(buffer.duration, this.loops.get(track));
    if (span) { src.loopStart = span.start; src.loopEnd = span.end; }
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.linearRampToValueAtTime(1, now + MusicBeds.FadeSeconds);
    src.connect(gain).connect(bus);
    const at = from ?? span?.start ?? 0;
    src.start(now, at);
    // A looping source ends only when stopped, but WebKit can end it under us (a context it tore down in the
    // background): forget it then, so the next `music(track)` (on resume) starts the bed again instead of trusting a
    // dead source. A bed we faded out or replaced is no longer `playing`, so its own end changes nothing.
    src.onended = () => { if (this.playing?.src === src) this.playing = null; };
    this.playing = { src, gain, track, from: at, startedAt: now, duration: buffer.duration, span };
    // The outgoing source holds its own buffer until its fade ends; the map lets go of it now.
    for (const other of TRACKS) if (other !== track) this.beds.delete(other);
  }

  /**
   * The context was replaced (audioLifecycle.ts): the bed's source died with the old one. The bed wanted starts again
   * on the new context where it had got to (`deadNow`: the old context's last time), fading in; the decoded buffer
   * carries over (an AudioBuffer belongs to no context), a decode in flight on the old one is started again.
   */
  reattach(deadNow: number): void {
    const was = this.playing;
    this.playing = null;
    this.generation++;
    this.decoding.clear();
    try { was?.src.stop(); } catch { /* the old context is closed: nothing left to stop */ }
    if (!this.wanted) return;
    const from = was && was.track === this.wanted ? bedPosition(was.from, deadNow - was.startedAt, was.duration, was.span) : undefined;
    this.music(this.wanted, from);
  }

  private fadeOutBed(): void {
    const ctx = this.out()?.ctx;
    if (!ctx || !this.playing) return;
    const now = ctx.currentTime;
    const old = this.playing;
    old.gain.gain.cancelScheduledValues(now);
    old.gain.gain.setValueAtTime(old.gain.gain.value, now);
    old.gain.gain.linearRampToValueAtTime(0.0001, now + MusicBeds.FadeSeconds);
    old.src.stop(now + MusicBeds.FadeSeconds + 0.05);
    this.playing = null;
  }
}
